import { useState, useRef } from 'react';
import { Plus, Image as ImageIcon, Trash2, Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ConfirmationDialog } from '@/components/ui/confirmation-dialog';
import { EmptyState } from '@/components/ui/empty-state';
import {
  useMediaGallery,
  useCreateGalleryItem,
  useDeleteGalleryItem,
  useUploadMediaImage,
  type MediaGalleryItem,
} from '@/lib/api/hooks/use-media';
import { format } from 'date-fns';
import { motion } from 'framer-motion';
import { toast } from 'react-hot-toast';

export function GalleryTab() {
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<MediaGalleryItem | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data: items = [], isLoading } = useMediaGallery();
  const createItem = useCreateGalleryItem();
  const deleteItem = useDeleteGalleryItem();
  const uploadImage = useUploadMediaImage();

  const handleImageSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setIsUploading(true);
    try {
      // Upload all selected images
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const result = await uploadImage.mutateAsync({ file, folder: 'media/gallery' });
        await createItem.mutateAsync({ imageUrl: result.url });
      }
      toast.success(`${files.length} image${files.length > 1 ? 's' : ''} uploaded successfully`);

      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
    } finally {
      setIsUploading(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedItem) return;
    await deleteItem.mutateAsync(selectedItem.id);
    setIsDeleteDialogOpen(false);
    setSelectedItem(null);
  };

  if (isLoading) {
    return <div className="text-foreground">Loading...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Upload Button */}
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold text-foreground">Photo Gallery</h3>
          <p className="text-sm text-muted-foreground">Upload images to display in the gallery</p>
        </div>
        <div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            onChange={handleImageSelect}
            className="hidden"
            id="gallery-upload"
          />
          <Button
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading}
          >
            {isUploading ? (
              <>
                <Upload className="w-4 h-4 mr-2 animate-spin" />
                Uploading...
              </>
            ) : (
              <>
                <Plus className="w-4 h-4 mr-2" />
                Upload Images
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Gallery Grid */}
      {items.length === 0 ? (
        <EmptyState
          icon={ImageIcon}
          title="No gallery images yet"
          description="Click the button above to upload images to your gallery."
          action={{
            label: 'Upload Images',
            onClick: () => fileInputRef.current?.click(),
          }}
        />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {items.map((item) => (
            <motion.div
              key={item.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-card border border-border rounded-lg overflow-hidden group hover:shadow-lg transition-shadow relative"
            >
              <div className="aspect-square relative overflow-hidden bg-muted">
                <img
                  src={item.imageUrl}
                  alt="Gallery image"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
                {/* Delete button overlay */}
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => {
                      setSelectedItem(item);
                      setIsDeleteDialogOpen(true);
                    }}
                  >
                    <Trash2 className="w-4 h-4 mr-1" />
                    Delete
                  </Button>
                </div>
              </div>
              <div className="p-2 text-center">
                <p className="text-xs text-muted-foreground">
                  {format(new Date(item.createdAt), 'MMM dd, yyyy')}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Delete Confirmation */}
      <ConfirmationDialog
        isOpen={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        onConfirm={handleDelete}
        title="Delete Image"
        description="Are you sure you want to delete this image? This action cannot be undone."
      />
    </div>
  );
}
